<template>
  <q-table
    v-if="isGranted('VIEW_CLIENTS')"
    :columns="columns"
    :rows="clients"
    v-model:pagination="pagination"
    :loading="isLoading"
    v-bind="$attrs"
    @request="pagination = $event.pagination"
  >
    <template v-slot:top>
      <add-client-btn
        :customer-id="customerId"
        label="Add Client"
        color="primary"
        rounded
        class="on-left"
        @added="prepend"
      />
      <q-input
        v-model="keyword"
        placeholder="Name, email, file ref no."
        clear-icon="fa fa-times"
        clearable
        style="min-width: 320px"
        filled
        dense
        autocomplete="no"
        @clear="keyword = ''">
        <template v-slot:prepend>
          <q-icon name="fa fa-search" />
        </template>
      </q-input>
    </template>
    <template v-slot:body-cell-name="props">
      <q-td :props="props">
        <app-user v-slot="{ isServiceProvider }">
          <router-link
            v-if="isServiceProvider"
            :to="`/admin/accounts/${props.row.customerId}/clients/${props.row.id}`"
            class="link"
          >
            {{ props.value }}
          </router-link>
          <router-link
            v-else
            :to="{name: 'client_profile', params: {clientId: props.row.id}}"
            class="link"
            title="View Profile"
          >
            {{ props.value }}
          </router-link>
        </app-user>
      </q-td>
    </template>
    <template v-slot:body-cell-status="props">
      <q-td :props="props">
        <user-status
          v-model:user="props.row.user"
          user-type="Client"
          @update:user="setUser($event)"
        />
      </q-td>
    </template>
    <template v-slot:body-cell-createdAt="props">
      <q-td :props="props">
        <humanize-time :time="props.value" />
      </q-td>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <actions-dropdown :client="props.row" @edited="set" @deleted="remove" />
      </q-td>
    </template>
  </q-table>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import moment from 'moment';
import isGranted from '@/auth/isGranted';
import eventBus from '@/messaging/event-bus';
import { ClientEdited, ClientDeleted } from '@/messaging/events';
import {
  Client,
  QueryResult,
  QTablePagination,
  ClientFilters,
  User,
} from '@/type';
import { HasTaskMixin } from '@/mixins';
import HumanizeTime from '@/components/HumanizeTime.vue';
import UserStatus from '@/user/UserStatus.vue';
import AppUser from '@/auth/AppUser.vue';
import repository from './client-repository';
import AddClientBtn from './create/AddClientBtn.vue';
import ActionsDropdown from './ActionsDropdown.vue';
import { name as clientNameFilter } from './client-filter';

interface Data {
  columns: object[],
  pagination: QTablePagination,
  clients: Client[],
  keyword: string,
  task: Promise<QueryResult>|null,
}

export default defineComponent({
  mixins: [HasTaskMixin],
  components: {
    AppUser,
    UserStatus,
    HumanizeTime,
    AddClientBtn,
    ActionsDropdown,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      columns: [
        {
          name: 'name',
          label: 'Name',
          field: clientNameFilter,
          align: 'left',
        },
        {
          name: 'email',
          label: 'Email',
          field: 'email',
          align: 'left',
        },
        {
          name: 'dateOfBirth',
          label: 'Date of Birth',
          field: 'dateOfBirth',
          format: (dateOfBirth: string) => moment(dateOfBirth).format('MMM/DD/YYYY'),
          align: 'left',
        },
        {
          name: 'fileRefNo',
          label: 'File Ref No.',
          field: 'fileRefNo',
          align: 'left',
        },
        {
          name: 'dateOfLoss',
          label: 'Date of Loss',
          field: 'dateOfLoss',
          format: (dateOfLoss: string) => moment(dateOfLoss).format('MMM/DD/YYYY'),
          align: 'left',
        },
        {
          name: 'status',
          label: 'Status',
          align: 'center',
        },
        {
          name: 'createdAt',
          label: 'Created',
          field: 'createdAt',
          align: 'left',
        },
        {
          name: 'actions',
          label: '',
          align: 'right',
        },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        rowsNumber: 0,
      },
      clients: [],
      keyword: '',
      task: null,
    };
  },
  computed: {
    offset(): number {
      return (this.pagination.page - 1) * this.pagination.rowsPerPage;
    },
    filters(): ClientFilters {
      return {
        offset: this.offset,
        limit: this.pagination.rowsPerPage,
        keyword: this.keyword,
      };
    },
  },
  watch: {
    customerId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    filters: 'load',
  },
  methods: {
    load() {
      this.task = repository.findAll(this.customerId, this.filters);
      this.task.then((result: QueryResult) => {
        this.pagination.rowsNumber = result.total;
        this.clients = (result.items as Client[]);
      });
    },
    prepend(client: Client) {
      this.clients.unshift(client);
    },
    set(client: Client) {
      const index = this.clients.findIndex(c => c.id === client.id);
      if (index >= 0) {
        this.clients.splice(index, 1, client);
      }
    },
    remove(clientId: number) {
      const index = this.clients.findIndex(c => c.id === clientId);
      if (index >= 0) {
        this.pagination.rowsNumber -= 1;
        this.clients.splice(index, 1);
      }
    },
    onClientEdited(event: ClientEdited) {
      this.set(event.client);
    },
    onClientDeleted(event: ClientDeleted) {
      this.remove(event.clientId);
    },
    setUser(user: User) {
      const client = this.clients.find(c => c.user.id === user.id);
      if (client) {
        client.user = user;
      }
    },
    isGranted,
  },
  created() {
    eventBus.$on(ClientEdited.eventName, this.onClientEdited);
    eventBus.$on(ClientDeleted.eventName, this.onClientDeleted);
  },
  beforeUnmount() {
    eventBus.$off(ClientEdited.eventName, this.onClientEdited);
    eventBus.$off(ClientDeleted.eventName, this.onClientDeleted);
  },
});
</script>
